import { useState, useEffect, useMemo, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'sonner';

import { useContratoFormData } from '@/hooks/useContratoFormData';
import { useProfileSwitcher } from '@/hooks/useProfileSwitcher';
import { useAuth } from '@/features/auth';
import { formConfigs, FieldConfig } from '@/config/formConfig';
import { unifiedContratoService, unifiedServices } from '@/services/unifiedService';

const NON_CLIENTE_FIELD = (fieldId: string) =>
    fieldId !== 'cliente_search' && fieldId !== 'cliente_id' && fieldId !== 'cliente_verificacao';

interface UseInlineContratoFormProps {
    clienteId: string;
    /** Contrato já existente para este cliente (relação card↔contrato é 1x1) — presença = modo edição */
    contratoId?: string;
    onSaved?: (contrato: any) => void;
}

export function useInlineContratoForm({ clienteId, contratoId, onSaved }: UseInlineContratoFormProps) {
    const { profile } = useAuth();
    const { getCurrentProfile, isSimulating } = useProfileSwitcher();

    const currentRole = getCurrentProfile;
    const formConfig = useMemo(() => {
        return formConfigs[currentRole as keyof typeof formConfigs] || formConfigs.USER;
    }, [currentRole]);

    // Seções do formulário embutido = steps do config, exceto o step "cliente" (já é conhecido)
    const sections = useMemo(
        () => formConfig.steps.filter((step) => step.id !== 'cliente'),
        [formConfig]
    );

    const inlineFields = useMemo(() => {
        const entries = Object.entries(formConfig.fields).filter(([id]) => NON_CLIENTE_FIELD(id));
        return Object.fromEntries(entries) as Record<string, FieldConfig>;
    }, [formConfig]);

    // Contrato ativo: começa com o que veio por prop e passa a apontar para o
    // recém-criado assim que o create termina, sem depender do refetch do pai
    // (evita criar um segundo contrato duplicado se o usuário submeter de novo rápido)
    const [activeContratoId, setActiveContratoId] = useState<string | undefined>(contratoId);
    const isEditing = !!activeContratoId;

    useEffect(() => {
        if (contratoId && contratoId !== activeContratoId) {
            setActiveContratoId(contratoId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [contratoId]);

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isLoadingContrato, setIsLoadingContrato] = useState(!!contratoId);

    const tenantId = profile?.tenant_id || null;
    const {
        grupos,
        bens,
        consultores: allConsultores,
        isLoading: loadingData,
        hasError: hasDataError,
        loadingGrupos,
        loadingBens,
        loadingConsultores,
        errorGrupos,
        errorBens,
        errorClientes,
        errorConsultores,
    } = useContratoFormData(tenantId);

    const consultores = useMemo(() => {
        if (!allConsultores) return [];
        if (currentRole === 'SUPERVISOR' && profile?.uuid) {
            const supervisorRec = allConsultores.find((c: any) => c.user_id === profile.uuid);
            if (supervisorRec) {
                return allConsultores.filter((c: any) =>
                    c.supervisor_id === supervisorRec.id && c.tipo === 'lider'
                );
            }
        }
        return allConsultores;
    }, [allConsultores, currentRole, profile?.uuid]);

    // Schema dinâmico — campos de moeda/numéricos usam number, o resto string
    const createSchema = () => {
        const schemaFields: Record<string, any> = {};

        Object.entries(inlineFields).forEach(([key, field]) => {
            if (field.type === 'file') {
                schemaFields[key] = z.any().optional();
            } else if (field.validation?.custom === 'currency_validation') {
                schemaFields[key] = field.required
                    ? z.number().positive(`${field.label} é obrigatório`)
                    : z.number().optional();
            } else if (field.required) {
                schemaFields[key] = z.string().min(1, `${field.label} é obrigatório`);
            } else {
                schemaFields[key] = z.string().optional();
            }
        });

        return z.object(schemaFields);
    };

    const buildDefaultValues = () =>
        Object.fromEntries(
            Object.entries(inlineFields).map(([key, field]) => [
                key,
                field.validation?.custom === 'currency_validation' ? 0 : '',
            ])
        );

    const form = useForm({
        resolver: zodResolver(createSchema()),
        defaultValues: buildDefaultValues(),
    });

    // Enquanto true, os efeitos de auto-cálculo/limpeza abaixo não sobrescrevem valores
    // (usado tanto no boot inicial quanto logo após carregar um contrato para edição)
    const isInitialLoading = useRef(true);

    const calcularTaxaMediaGrupo = (grupo: any) => {
        if (!grupo?.id) return null;

        if (grupo.tx_adm_total) {
            const taxaStr = grupo.tx_adm_total.toString();
            const taxaNum = parseFloat(taxaStr.replace(',', '.').replace(' %', ''));
            return taxaNum.toString();
        }

        if (grupo.taxas && Array.isArray(grupo.taxas)) {
            const taxaAdmTotal = grupo.taxas.find((taxa: any) =>
                taxa.nome === 'Tx.Adm Total' || taxa.tipo === 'ADMINISTRACAO'
            );
            if (taxaAdmTotal?.percentual) {
                return taxaAdmTotal.percentual.toString();
            }
        }

        return "2.0";
    };

    // Carregar dados do contrato existente (modo edição)
    useEffect(() => {
        if (!activeContratoId || loadingData) return;

        let cancelled = false;
        isInitialLoading.current = true;
        setIsLoadingContrato(true);

        unifiedContratoService.findById(activeContratoId).then((contrato: any) => {
            if (cancelled || !contrato) return;

            const grupoIdStr = contrato.grupo_id?.toString() || '';
            const grupoObj = grupoIdStr && grupos ? grupos.find((g: any) => g.id.toString() === grupoIdStr) : null;
            const grupoSource = grupoObj || contrato.grupos;

            const bemIdStr = contrato.bem_id?.toString() || '';
            const bemObj = bemIdStr && bens ? bens.find((b: any) => b.id.toString() === bemIdStr) : null;
            const bemSource = bemObj || contrato.bens;

            let taxaAdm = '';
            let valorTotal = '';
            if (grupoSource && bemSource) {
                const taxaPercentual = parseFloat(calcularTaxaMediaGrupo(grupoSource) || '0');
                const valorBem = bemSource.preco_bem || 0;
                const valorTaxa = valorBem * (taxaPercentual / 100);
                taxaAdm = taxaPercentual.toString();
                valorTotal = (valorBem + valorTaxa).toString();
            }

            form.reset({
                ...buildDefaultValues(),
                grupo_id: grupoIdStr,
                grupo_nome: grupoSource?.especie || grupoSource?.nome || '',
                bem_id: bemIdStr,
                bem_nome: bemSource?.tipo_bem || bemSource?.nome || '',
                taxa_adm: taxaAdm,
                valor_total: valorTotal,
                valorParcela: Number(contrato.valor_parcela) || 0,
                valor_entrada: Number(contrato.valor_entrada) || 0,
                metodoPagamento: contrato.metodo_pagamento || '',
                observacoes: contrato.observacoes || '',
                contrato_numero: contrato.contrato_numero || '',
                cota: contrato.cota || '',
                consultor_id: contrato.consultor_id?.toString() || '',
                meses_restantes: contrato.parcelas_restantes?.toString() || '',
            });
        }).catch((error) => {
            console.error('Erro ao carregar contrato para edição:', error);
            toast.error('Não foi possível carregar os dados do contrato.');
        }).finally(() => {
            if (cancelled) return;
            setIsLoadingContrato(false);
            setTimeout(() => {
                if (!cancelled) isInitialLoading.current = false;
            }, 300);
        });

        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeContratoId, loadingData]);

    // Auto-selecionar consultor para LIDER_VENDAS (apenas em criação)
    useEffect(() => {
        if (isEditing) return;
        if (currentRole === 'LIDER_VENDAS' && profile?.uuid && allConsultores && allConsultores.length > 0) {
            const currentConsultor = allConsultores.find((c: any) => c.user_id === profile.uuid);

            if (currentConsultor && !form.getValues('consultor_id')) {
                form.setValue('consultor_id', currentConsultor.id.toString());
            }
        }
    }, [isEditing, currentRole, profile?.uuid, allConsultores, form]);

    // Limpar bem quando grupo mudar + preencher meses restantes
    useEffect(() => {
        const subscription = form.watch((values, { name }) => {
            if (name === 'grupo_id') {
                const grupoId = values.grupo_id;

                if (!isInitialLoading.current) {
                    form.setValue('bem_id', '');
                    form.setValue('taxa_adm', '');
                    form.setValue('valor_total', '');
                }

                if (grupoId && grupos) {
                    const grupoSelecionado = grupos.find((grupo: any) => grupo.id.toString() === grupoId);
                    if (grupoSelecionado) {
                        form.setValue('grupo_nome', grupoSelecionado.especie || grupoSelecionado.nome || '');

                        if (grupoSelecionado.meses_restantes !== undefined && grupoSelecionado.meses_restantes !== null) {
                            form.setValue('meses_restantes', grupoSelecionado.meses_restantes.toString());
                        }
                    }
                }
            } else if (name === 'bem_id') {
                const bemId = values.bem_id;
                if (bemId && bens) {
                    const bemSelecionado = bens.find((bem: any) => bem.id.toString() === bemId);
                    if (bemSelecionado) {
                        form.setValue('bem_nome', bemSelecionado.tipo_bem || bemSelecionado.nome || '');
                    }
                }
            }
        });

        if (!loadingData && !activeContratoId && isInitialLoading.current) {
            setTimeout(() => {
                isInitialLoading.current = false;
            }, 1000);
        }

        return () => subscription.unsubscribe();
    }, [form, grupos, bens, loadingData, activeContratoId]);

    // Calcular valores automaticamente quando bem for selecionado (não roda durante carga inicial/edição)
    useEffect(() => {
        const bemId = form.watch('bem_id');
        const grupoId = form.watch('grupo_id');

        if (isInitialLoading.current) return;

        if (bemId && bens && grupos) {
            const bemSelecionado = bens.find((bem: any) => bem.id.toString() === bemId);
            const grupoSelecionado = grupos.find((grupo: any) => grupo.id.toString() === grupoId);

            if (bemSelecionado && grupoSelecionado) {
                const valorParcelaEncontrado = bemSelecionado.parcela_ate_contemplacao || bemSelecionado.valor_parcela || 0;
                if (valorParcelaEncontrado && !isNaN(Number(valorParcelaEncontrado)) && Number(valorParcelaEncontrado) > 0) {
                    form.setValue('valorParcela', Number(valorParcelaEncontrado));
                } else {
                    form.setValue('valorParcela', 0);
                }

                const valorEntradaEncontrado = bemSelecionado.primeira_parcela_ate_contemplacao || 0;
                if (valorEntradaEncontrado && !isNaN(Number(valorEntradaEncontrado)) && Number(valorEntradaEncontrado) > 0) {
                    form.setValue('valor_entrada', Number(valorEntradaEncontrado));
                } else {
                    form.setValue('valor_entrada', 0);
                }

                const taxaPercentual = parseFloat(calcularTaxaMediaGrupo(grupoSelecionado) || '0');
                const valorBem = bemSelecionado.preco_bem || 0;
                const valorTaxa = valorBem * (taxaPercentual / 100);
                const valorTotal = valorBem + valorTaxa;

                form.setValue('taxa_adm', taxaPercentual.toString());
                form.setValue('valor_total', valorTotal.toString());
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [form.watch('bem_id'), bens, grupos]);

    // Campos visíveis por seção, na mesma ordem/priority usada no wizard original
    const sectionFields = useMemo(() => {
        const priorityOrder: Record<string, number> = { high: 0, medium: 1, low: 2 };
        const result: Record<string, FieldConfig[]> = {};

        sections.forEach((section) => {
            result[section.id] = section.fields
                .map((fieldId) => inlineFields[fieldId])
                .filter((field): field is FieldConfig => !!field && field.visible)
                .sort((a, b) => priorityOrder[a.priority] - priorityOrder[b.priority]);
        });

        return result;
    }, [sections, inlineFields]);

    const fieldToSectionMap = useMemo(() => {
        const map: Record<string, string> = {};
        sections.forEach((section) => {
            section.fields.forEach((fieldId) => {
                if (inlineFields[fieldId]) map[fieldId] = section.id;
            });
        });
        return map;
    }, [sections, inlineFields]);

    const buildContratoPayload = (data: Record<string, any>) => {
        const selectedBem = data.bem_id && bens
            ? bens.find((b: any) => b.id.toString() === data.bem_id.toString())
            : null;
        const valorBem = selectedBem?.preco_bem || null;

        return {
            cliente_id: parseInt(clienteId, 10),
            grupo_id: data.grupo_id ? parseInt(data.grupo_id, 10) : null,
            bem_id: data.bem_id ? parseInt(data.bem_id, 10) : null,
            consultor_id: data.consultor_id ? parseInt(data.consultor_id, 10) : null,
            valor_bem: valorBem,
            valor_parcela: typeof data.valorParcela === 'number' ? data.valorParcela : null,
            valor_entrada: typeof data.valor_entrada === 'number' ? data.valor_entrada : null,
            metodo_pagamento: data.metodoPagamento || 'PIX',
            observacoes: data.observacoes || '',
            cota: data.cota || null,
            parcelas_restantes: data.meses_restantes ? parseInt(data.meses_restantes, 10) : null,
        };
    };

    const syncMesesRestantes = async (data: Record<string, any>) => {
        if (data.meses_restantes && data.grupo_id) {
            try {
                const mesesVal = parseInt(data.meses_restantes, 10);
                if (!isNaN(mesesVal)) {
                    await unifiedServices.grupos.update(data.grupo_id, { meses_restantes: mesesVal });
                }
            } catch (gError) {
                // Silencia — perfil pode não ter permissão de escrita global em grupos (RLS)
            }
        }
    };

    const handleSubmit = async (data: Record<string, any>) => {
        try {
            setIsSubmitting(true);

            const isValid = await form.trigger();
            if (!isValid) {
                toast.error('Existem campos obrigatórios não preenchidos. Revise as seções destacadas em vermelho.');
                return;
            }

            let contratoNumero = data.contrato_numero || '';
            if (!contratoNumero || contratoNumero.trim() === '') {
                if (profile?.role === 'RELACIONADOR') {
                    toast.error("O código do contrato é obrigatório para seu perfil. Preencha o campo 'Número do Contrato' antes de enviar.");
                    return;
                }
                contratoNumero = `CONT_${new Date().getFullYear()}_${Date.now()}`;
            }

            let contrato: any;

            if (activeContratoId) {
                const payload = { ...buildContratoPayload(data), contrato_numero: contratoNumero };
                contrato = await unifiedContratoService.update(activeContratoId, payload);
                await syncMesesRestantes(data);
                toast.success('Contrato atualizado com sucesso!');
            } else {
                let statusInicial = 'ATIVO';
                if (profile?.role === 'LIDER_VENDAS' || profile?.role === 'RELACIONADOR') {
                    statusInicial = 'em_andamento';
                }

                const payload = {
                    ...buildContratoPayload(data),
                    contrato_numero: contratoNumero,
                    status: statusInicial,
                    created_by: profile?.uuid,
                    tenant_id: profile?.tenant_id,
                    cadastro_feito_dia: new Date().toISOString().split('T')[0],
                    tabela_negociacao: 'PADRAO',
                    status_pagamento: 'AGUARDANDO_ENTRADA',
                    total_pago: 0,
                    parcelas_pagas: 0,
                    reducao: false,
                    embutida: false,
                    pessoa_politicamente_exposta: false,
                };

                contrato = await unifiedContratoService.create(payload);
                await syncMesesRestantes(data);
                toast.success('Contrato criado com sucesso!');
                setActiveContratoId(contrato.id.toString());
            }

            onSaved?.(contrato);
        } catch (error) {
            console.error(`Erro ao ${activeContratoId ? 'atualizar' : 'criar'} contrato:`, error);
            const acao = activeContratoId ? 'atualizar' : 'criar';
            const errorMessage = error instanceof Error ? error.message : 'Erro desconhecido';
            toast.error(`Não foi possível ${acao} o contrato: ${errorMessage}. Verifique os dados e tente novamente.`);
        } finally {
            setIsSubmitting(false);
        }
    };

    return {
        form,
        formConfig,
        currentRole,
        isSimulating,

        sections,
        sectionFields,
        fieldToSectionMap,

        loadingData,
        hasDataError,
        loadingGrupos,
        loadingBens,
        loadingConsultores,
        errorGrupos,
        errorBens,
        errorClientes,
        errorConsultores,

        grupos,
        bens,
        consultores,

        isEditing,
        isLoadingContrato,
        isSubmitting,

        handleSubmit,
        calcularTaxaMediaGrupo,

        profile,
    };
}
